/**
 * SessionProvider - PROJ-035: Session Expiration Handling
 *
 * Global provider that:
 * 1. Initializes proactive token management (useTokenManager)
 * 2. Listens for session events
 * 3. Shows SessionExpiredModal when session expires
 * 4. Provides session state to child components
 * 5. Synchronizes logout events across multiple tabs
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useTokenManager, useSessionEvents, SESSION_EVENTS } from '@/hooks/useTokenManager';
import { SessionExpiredModal } from './SessionExpiredModal';
import { SessionExpiryWarning } from './SessionExpiryWarning'; // PROJ-035
import { getCachedUser } from '@/lib/auth';
import { tabSync } from '@/lib/tabSync';

interface SessionContextValue {
  /** Whether session is currently valid */
  isSessionValid: boolean;
  /** Whether session is about to expire (warning state) */
  isExpiringSoon: boolean;
  /** Manually trigger session expiration (for testing) */
  triggerExpired: () => void;
  /** Seconds until token expires */
  secondsUntilExpiry: number | null;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState<number | null>(null);
  const [userEmail, setUserEmail] = useState<string | undefined>();
  // PROJ-035: Track expiration type
  const [expirationType, setExpirationType] = useState<'idle' | 'absolute' | 'unknown'>('unknown');

  // Initialize token manager for proactive refresh
  const { getSecondsUntilExpiry } = useTokenManager();

  // Handle session events
  useSessionEvents({
    onExpired: useCallback(() => {
      const cachedUser = getCachedUser();
      setUserEmail(cachedUser?.email);
      setExpirationType('unknown'); // Will be updated by specific events if available
      setShowExpiredModal(true);
      setIsExpiringSoon(false);
    }, []),

    onExpiringSoon: useCallback(() => {
      setIsExpiringSoon(true);
    }, []),

    onRefreshed: useCallback(() => {
      setShowExpiredModal(false);
      setIsExpiringSoon(false);
      setExpirationType('unknown');
    }, []),

    // PROJ-035: Track specific expiration types
    onIdleExpiringSoon: useCallback(() => {
      setExpirationType('idle');
      setIsExpiringSoon(true);
    }, []),

    onAbsoluteExpiringSoon: useCallback(() => {
      setExpirationType('absolute');
      setIsExpiringSoon(true);
    }, []),
  });

  // Update seconds until expiry periodically
  useEffect(() => {
    const updateExpiry = () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        const seconds = getSecondsUntilExpiry(accessToken);
        setSecondsUntilExpiry(seconds);
      } else {
        setSecondsUntilExpiry(null);
      }
    };

    updateExpiry();
    const interval = setInterval(updateExpiry, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [getSecondsUntilExpiry]);

  // Listen for direct SESSION_EXPIRED events (from 401 interceptor)
  useEffect(() => {
    const handleExpired = () => {
      const cachedUser = getCachedUser();
      setUserEmail(cachedUser?.email);
      setShowExpiredModal(true);
    };

    // PROJ-035: Listen for specific expiration type events
    const handleIdleExpired = () => {
      setExpirationType('idle');
      const cachedUser = getCachedUser();
      setUserEmail(cachedUser?.email);
      setShowExpiredModal(true);
    };

    const handleAbsoluteExpired = () => {
      setExpirationType('absolute');
      const cachedUser = getCachedUser();
      setUserEmail(cachedUser?.email);
      setShowExpiredModal(true);
    };

    window.addEventListener(SESSION_EVENTS.EXPIRED, handleExpired);
    window.addEventListener('session:idle-expired', handleIdleExpired);
    window.addEventListener('session:absolute-expired', handleAbsoluteExpired);

    return () => {
      window.removeEventListener(SESSION_EVENTS.EXPIRED, handleExpired);
      window.removeEventListener('session:idle-expired', handleIdleExpired);
      window.removeEventListener('session:absolute-expired', handleAbsoluteExpired);
    };
  }, []);

  const handleModalClose = useCallback(() => {
    setShowExpiredModal(false);
    // Update user email from fresh cache after re-auth
    const cachedUser = getCachedUser();
    setUserEmail(cachedUser?.email);
  }, []);

  const triggerExpired = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
  }, []);

  const contextValue: SessionContextValue = {
    isSessionValid: !showExpiredModal,
    isExpiringSoon,
    triggerExpired,
    secondsUntilExpiry,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
      <SessionExpiredModal
        open={showExpiredModal}
        onClose={handleModalClose}
        userEmail={userEmail}
        expirationType={expirationType}
      />
      {/* PROJ-035: Show warning toast for idle/absolute expiration */}
      <SessionExpiryWarning />
    </SessionContext.Provider>
  );
}

/**
 * Hook to access session context
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

/**
 * Hook for components that want optional session awareness
 * (won't throw if used outside provider)
 */
export function useSessionOptional(): SessionContextValue | null {
  return useContext(SessionContext);
}

export default SessionProvider;
